import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sword, Shield, Crown, Users, Calendar, Trophy, 
  Star, MessageCircle, Gamepad2, Sparkles, ArrowRight, Play, Flame, Zap, X, Clock, MapPin
} from 'lucide-react';

// RPG World Data - Shortened for mobile
const RPG_WORLD = {
  title: "ANIMAC Metaverse RPG",
  subtitle: "Your Adventure Awaits",
  description: "Step into an immersive RPG world. Build your character, join clans, and forge alliances with fellow anime enthusiasts.",
  features: [
    { icon: Sword, title: "Epic Quests", desc: "Storyline-driven missions" },
    { icon: Shield, title: "Progression", desc: "Level up & unlock abilities" },
    { icon: Crown, title: "Clan System", desc: "Form alliances" },
    { icon: Users, title: "Social Hub", desc: "Meet adventurers" }
  ]
};

// Weekly Activities Data - With detailed info for modal
const WEEKLY_ACTIVITIES = [
  {
    id: 1,
    day: "Sat",
    title: "Game Night",
    time: "8PM - 11PM",
    description: "Multiplayer gaming sessions for all skill levels.",
    fullDescription: "Join fellow gamers for exciting multiplayer sessions every Saturday! Whether you're into competitive ranked matches or casual gameplay, there's always a lobby waiting for you. We feature popular titles including fighting games, RPGs, and party games. Make new friends and test your skills against players from around the community!",
    icon: Gamepad2,
    color: "from-purple-500 to-pink-500",
    participants: 150,
    requirements: "Stable internet connection, microphone recommended",
    schedule: "Every Saturday"
  },
  {
    id: 2,
    day: "Fri",
    title: "Debate Wars",
    time: "7PM - 9PM",
    description: "Passionate debates about anime & entertainment.",
    fullDescription: "Get ready for heated but friendly debates about anime, movies, cartoons, and all things entertainment! Topics range from 'Best anime of all time' to 'Studio comparisons' and 'Character rankings'. Present your arguments, back them up with evidence, and maybe even change some minds. All opinion levels welcome!",
    icon: MessageCircle,
    color: "from-blue-500 to-cyan-500",
    participants: 85,
    requirements: "Nothing required - just bring your opinions!",
    schedule: "Every Friday"
  },
  {
    id: 3,
    day: "Sun",
    title: "Watch Party",
    time: "6PM - 10PM",
    description: "Synchronized anime & movie viewings.",
    fullDescription: "Experience anime and movies together with the community! We synchronize viewing across our platform and provide live commentary, fun facts, and discussion breaks. This week features a mix of classic anime and trending shows. Snacks strongly recommended!",
    icon: Play,
    color: "from-yellow-500 to-orange-500",
    participants: 200,
    requirements: "Netflix/Crunchyroll account for syncing, or just watch along!",
    schedule: "Every Sunday"
  },
  {
    id: 4,
    day: "Wed",
    title: "Creator Showcase",
    time: "5PM - 7PM",
    description: "Share artwork, cosplay & creative projects.",
    fullDescription: "Our weekly spotlight on community talent! Artists, cosplayers, writers, and musicians share their work with the community. Get feedback, find collaborators, and celebrate creative achievements. Whether you're a seasoned creator or just starting out, this is your stage!",
    icon: Star,
    color: "from-green-500 to-teal-500",
    participants: 60,
    requirements: "Work to showcase (optional), positive attitude!",
    schedule: "Every Wednesday"
  }
];

// Special Events Data - With detailed info for modal
const SPECIAL_EVENTS = [
  {
    id: 1,
    type: "themed-month",
    title: "Naruto Month",
    month: "March 2026",
    description: "Month-long Naruto celebration with tournaments & watch parties.",
    fullDescription: "A spectacular month dedicated to the legendary Naruto series! Enjoy special Naruto-themed tournaments, watch parties covering all episodes, debate sessions about favorite moments, and exclusive community-created content showcases. Special prizes for participants include limited badges and featured spots on the website!",
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800x400",
    color: "from-orange-500 to-red-500",
    schedule: "March 1-31, 2026"
  },
  {
    id: 2,
    type: "special-day",
    title: "Anime Day",
    date: "Oct 15, 2026",
    description: "24-hour anime marathon with special guests.",
    fullDescription: "International Anime Day comes to ANIMAC! Join our 24-hour marathon featuring screenings of iconic anime films, Q&A sessions with voice actor guests (TBA), trivia competitions with exclusive prizes, and community appreciation events. Don't miss this celebration of anime culture!",
    image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800x400",
    color: "from-pink-500 to-purple-500",
    schedule: "October 15, 2026"
  },
  {
    id: 3,
    type: "themed-month",
    title: "Ghibli Fest",
    month: "April 2026",
    description: "Magical Ghibli movie screenings & art showcases.",
    fullDescription: "Immerse yourself in the enchanting world of Studio Ghibli! Throughout April, we'll screen beloved classics like Spirited Away, My Neighbor Totoro, and more. Participate in art contests inspired by Ghibli's beautiful animation style, and join discussion panels about the themes and legacy of these masterpieces.",
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800x400",
    color: "from-blue-500 to-cyan-500",
    schedule: "April 1-30, 2026"
  }
];

// Tournament Options Data - With detailed info for modal
const TOURNAMENTS = [
  {
    id: 1,
    title: "Clan Wars",
    type: "Clan vs Clan",
    description: "Form your clan and battle for dominance!",
    fullDescription: "Assemble your team and compete for clan supremacy! Clan Wars are weekly tournaments where teams of 4-6 members face off in various games. Winning clans earn exclusive titles, profile badges, and bragging rights. Form alliances, develop strategies, and dominate the leaderboard!",
    icon: Users,
    color: "from-red-500 to-orange-500",
    prize: "Exclusive Titles & Badges",
    schedule: "Weekly on Saturdays",
    requirements: "Minimum 4 clan members, Discord required"
  },
  {
    id: 2,
    title: "Arena Champions",
    type: "PvP",
    description: "Test your skills in 1v1 competitions!",
    fullDescription: "Are you the best of the best? Prove it in our Arena Champions tournament! Compete in 1v1 matches across multiple game titles. Climb the seasonal leaderboard, earn ranking points, and become the ultimate champion. Weekly prizes include premium memberships and exclusive in-game items!",
    icon: Trophy,
    color: "from-yellow-500 to-amber-500",
    prize: "Premium Membership",
    schedule: "Weekly on Sundays",
    requirements: "Ranked account in good standing"
  },
  {
    id: 3,
    title: "Speed Runs",
    type: "Time Attack",
    description: "Race to complete challenges fastest!",
    fullDescription: "Speed is everything! Compete to complete gaming challenges in the fastest time possible. Whether it's finishing a game, achieving specific goals, or reaching certain milestones, speedrunners compete for the top spot. New challenges each week with varying difficulty levels!",
    icon: Zap,
    color: "from-purple-500 to-blue-500",
    prize: "In-Game Currency",
    schedule: "Bi-weekly on Wednesdays",
    requirements: "Recording software for verification"
  },
  {
    id: 4,
    title: "Art Battle",
    type: "Creative",
    description: "Showcase your artistic talents!",
    fullDescription: "Creativity takes center stage! Our Art Battle challenges artists to create themed pieces within a time limit. Fan art, original characters, cosplay, music, and crafts all compete for community votes. Winners get featured spots on the ANIMAC website and social media shoutouts!",
    icon: Flame,
    color: "from-pink-500 to-red-500",
    prize: "Website Feature",
    schedule: "Monthly on the last Friday",
    requirements: "Original work only, submission before deadline"
  }
];

// Creators Program Data
const CREATORS = [
  {
    id: 1,
    name: "AnimeArtist_99",
    specialty: "Digital Art",
    followers: "15K",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=anime1",
    featured: true,
    bio: "Creating stunning anime-inspired digital art for over 5 years. Specializes in character designs and fan art.",
    social: "@AnimeArtist99"
  },
  {
    id: 2,
    name: "CosplayQueen",
    specialty: "Cosplay",
    followers: "22K",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=cosplay1",
    featured: true,
    bio: "Award-winning cosplayer known for detailed costumes and amazing photography.",
    social: "@CosplayQueen"
  },
  {
    id: 3,
    name: "MangaReviewer",
    specialty: "Content Creator",
    followers: "35K",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=manga1",
    featured: true,
    bio: "In-depth manga reviews and recommendations for every genre imaginable.",
    social: "@MangaReviewer"
  },
  {
    id: 4,
    name: "OST_Master",
    specialty: "Music",
    followers: "8K",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=music1",
    featured: false,
    bio: "Creating anime-inspired music and remixes. Check out original compositions!",
    social: "@OSTMaster"
  }
];

// Card Components - Wireframe style buttons
const FeatureCard = ({ icon: Icon, title, description, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    whileHover={{ y: -5 }}
    className="bg-black rounded-xl p-3 sm:p-4 border border-gray-800 hover:border-purple-500/50 transition-all duration-300 group"
  >
    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg border border-purple-500/50 flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-purple-500/20 transition-colors`}>
      <Icon className="text-purple-400" size={16} />
    </div>
    <h3 className="font-azonix font-bold text-sm sm:text-base text-white mb-1">{title}</h3>
    <p className="text-gray-500 text-xs sm:text-sm font-inter line-clamp-2">{description}</p>
  </motion.div>
);

const ActivityCard = ({ activity, delay, onClick }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    whileHover={{ scale: 1.02 }}
    className="bg-black rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
    onClick={onClick}
  >
    <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${activity.color}`} />
    <div className="p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase">{activity.day}</span>
        <div className="p-1.5 sm:p-2 rounded-lg border border-gray-700">
          <activity.icon className="text-gray-400" size={14} />
        </div>
      </div>
      <h3 className="font-azonix font-bold text-base sm:text-lg text-white mb-1">{activity.title}</h3>
      <p className="text-purple-400 text-xs sm:text-sm mb-2 font-inter">{activity.time}</p>
      <p className="text-gray-500 text-xs sm:text-sm mb-2 font-inter line-clamp-2">{activity.description}</p>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center text-gray-600 text-xs">
          <Users size={12} className="mr-1" />
          {activity.participants}+
        </div>
        <span className="text-xs text-purple-400 font-semibold">Learn More →</span>
      </div>
    </div>
  </motion.div>
);

const TournamentCard = ({ tournament, delay, onClick }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    whileHover={{ x: 5 }}
    className="bg-black rounded-xl p-3 sm:p-4 border border-gray-800 hover:border-yellow-500/50 transition-all duration-300 flex items-start gap-3 group cursor-pointer"
    onClick={onClick}
  >
    <div className="p-2 sm:p-3 rounded-xl border border-gray-700 group-hover:border-yellow-500/50 transition-colors">
      <tournament.icon className="text-gray-400" size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-xs font-semibold text-gray-500 uppercase truncate">{tournament.type}</span>
      </div>
      <h3 className="font-azonix font-bold text-sm sm:text-base text-white mb-0.5 truncate">{tournament.title}</h3>
      <p className="text-gray-500 text-xs mb-1 font-inter line-clamp-1">{tournament.description}</p>
      <div className="flex items-center gap-1">
        <Trophy size={12} className="text-yellow-500" />
        <span className="text-yellow-500 text-xs font-semibold truncate">{tournament.prize}</span>
      </div>
    </div>
  </motion.div>
);

const CreatorCard = ({ creator, delay, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    whileHover={{ y: -5 }}
    className="bg-black rounded-xl p-3 sm:p-4 border border-gray-800 hover:border-purple-500/50 transition-all duration-300 text-center group cursor-pointer"
    onClick={onClick}
  >
    <div className="relative mb-3 inline-block">
      <img 
        src={creator.avatar} 
        alt={creator.name}
        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-gray-800 group-hover:border-purple-500 transition-colors"
      />
      {creator.featured && (
        <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-yellow-500 rounded-full flex items-center justify-center">
          <Star size={8} className="text-black" fill="currentColor" />
        </div>
      )}
    </div>
    <h3 className="font-azonix font-bold text-sm text-white mb-0.5 truncate px-1">{creator.name}</h3>
    <p className="text-purple-400 text-xs mb-1">{creator.specialty}</p>
    <div className="flex items-center justify-center gap-1 text-gray-600 text-xs">
      <Users size={10} />
      {creator.followers}
    </div>
    <button className="mt-2 sm:mt-3 w-full py-1.5 sm:py-2 px-3 border border-purple-500/50 text-purple-300 rounded-lg text-xs font-semibold hover:bg-purple-500/20 transition-colors">
      View Profile
    </button>
  </motion.div>
);

// Detail Modal Component - Similar to Hero Section
const DetailModal = ({ item, type, onClose }) => {
  if (!item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-2"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-black rounded-xl sm:rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden border border-purple-500/30 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative flex-shrink-0">
          {(item.image || item.color) && (
            <div className={`h-24 sm:h-32 ${item.image ? '' : 'bg-gradient-to-r ' + item.color}`}>
              {item.image && (
                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
              )}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          <button 
            onClick={onClose} 
            className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all duration-300"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
          <div className="absolute bottom-3 left-3 right-3">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${item.color || 'from-purple-600 to-pink-600'} text-white mb-2`}>
              {item.type || (type === 'activity' ? 'Weekly Activity' : type === 'tournament' ? 'Tournament' : type === 'event' ? 'Special Event' : 'Creator')}
            </span>
            <h2 className="text-xl sm:text-2xl font-azonix font-bold text-white line-clamp-2">
              {item.title}
            </h2>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Time/Schedule Info */}
          {(item.time || item.schedule) && (
            <div className="flex items-center gap-3 mb-4 text-gray-300">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Clock size={16} className="text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{item.day ? 'Schedule' : 'When'}</p>
                <p className="text-sm font-medium">{item.time || item.schedule}</p>
              </div>
            </div>
          )}

          {/* Location Info */}
          {item.location && (
            <div className="flex items-center gap-3 mb-4 text-gray-300">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <MapPin size={16} className="text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="text-sm font-medium">{item.location}</p>
              </div>
            </div>
          )}

          {/* Participants/Followers */}
          {(item.participants || item.followers) && (
            <div className="flex items-center gap-3 mb-4 text-gray-300">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Users size={16} className="text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{item.participants ? 'Participants' : 'Followers'}</p>
                <p className="text-sm font-medium">{item.participants ? `${item.participants}+ participants` : item.followers}</p>
              </div>
            </div>
          )}

          {/* Full Description */}
          <div className="mb-4">
            <h3 className="text-sm font-azonix font-bold text-white mb-2">Details</h3>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed font-inter">
              {item.fullDescription || item.description}
            </p>
          </div>

          {/* Requirements */}
          {item.requirements && (
            <div className="mb-4">
              <h3 className="text-sm font-azonix font-bold text-white mb-2">Requirements</h3>
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed font-inter">
                {item.requirements}
              </p>
            </div>
          )}

          {/* Prize (for tournaments) */}
          {item.prize && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
              <Trophy size={20} className="text-yellow-500" />
              <div>
                <p className="text-xs text-yellow-500/70">Prize</p>
                <p className="text-sm font-bold text-yellow-500">{item.prize}</p>
              </div>
            </div>
          )}

          {/* Bio (for creators) */}
          {item.bio && (
            <div className="mb-4">
              <h3 className="text-sm font-azonix font-bold text-white mb-2">Bio</h3>
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed font-inter">
                {item.bio}
              </p>
            </div>
          )}

          {/* Social (for creators) */}
          {item.social && (
            <div className="flex items-center gap-2 text-purple-400 text-sm">
              <span>@{item.social.replace('@', '')}</span>
            </div>
          )}
        </div>

        {/* Action Button - Wireframe style */}
        <div className="p-4 border-t border-gray-800">
          <button 
            onClick={onClose}
            className="w-full py-2.5 border border-purple-500/50 text-purple-300 rounded-lg text-sm font-semibold hover:bg-purple-500/20 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component - Mobile optimized with wireframe buttons
const CommunityFeatures = () => {
  const [activeTab, setActiveTab] = useState('activities');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const tabs = [
    { id: 'activities', label: 'Weekly', icon: Calendar },
    { id: 'special', label: 'Events', icon: Sparkles },
    { id: 'tournaments', label: 'Tournaments', icon: Trophy },
    { id: 'creators', label: 'Creators', icon: Star }
  ];

  const handleItemClick = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setSelectedType(null);
  };

  return (
    <section className="relative py-12 sm:py-16 bg-gradient-to-b from-black via-black to-black overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-48 sm:w-64 h-48 sm:h-64 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-64 h-48 sm:h-64 bg-pink-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-3 sm:px-4">
        {/* Header - Mobile optimized */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-azonix font-bold mb-3 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
            Community Hub
          </h2>
          <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto font-inter px-2">
            Join thousands of anime fans! From game nights to competitions.
          </p>
        </motion.div>

        {/* RPG World Section - Mobile optimized */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-16"
        >
          <div className="bg-gradient-to-br from-purple-900/30 via-black to-pink-900/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-500/20">
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 border border-purple-500/50 rounded-full text-purple-300 text-xs sm:text-sm font-semibold mb-3">
                  <Gamepad2 size={14} />
                  <span className="hidden xs:inline">{RPG_WORLD.title}</span>
                  <span className="xs:hidden">RPG</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-azonix font-bold text-white mb-2">{RPG_WORLD.subtitle}</h3>
                <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-5 font-inter leading-relaxed">{RPG_WORLD.description}</p>
                {/* Wireframe button */}
                <button className="inline-flex items-center px-4 sm:px-5 py-2 sm:py-2.5 border border-purple-500/50 text-purple-300 text-xs sm:text-sm rounded-lg font-semibold hover:bg-purple-500/20 transition-colors">
                  Start <ArrowRight size={14} className="ml-1.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {RPG_WORLD.features.map((feature, index) => (
                  <FeatureCard key={index} {...feature} color="from-purple-500 to-pink-500" delay={index * 0.1} />
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation - Wireframe style buttons */}
        <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-purple-500/20 border border-purple-500/50 text-white'
                  : 'border border-gray-700 text-gray-500 hover:border-gray-500 hover:text-gray-300'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Weekly Activities */}
            {activeTab === 'activities' && (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                {WEEKLY_ACTIVITIES.map((activity, index) => (
                  <ActivityCard 
                    key={activity.id} 
                    activity={activity} 
                    delay={index * 0.1}
                    onClick={() => handleItemClick(activity, 'activity')} 
                  />
                ))}
              </div>
            )}

            {/* Special Events */}
            {activeTab === 'special' && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {SPECIAL_EVENTS.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -5 }}
                    className="bg-black rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
                    onClick={() => handleItemClick(event, 'event')}
                  >
                    <div className="relative h-24 sm:h-32 overflow-hidden">
                      <img 
                        src={event.image} 
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${event.color.replace('from-', 'border-').replace(' to-', ' ')} text-white`}>
                        {event.type === 'themed-month' ? '📅' : '🎉'}
                      </div>
                    </div>
                    <div className="p-3 sm:p-4">
                      <h3 className="font-azonix font-bold text-sm sm:text-base text-white mb-0.5">{event.title}</h3>
                      <p className="text-purple-400 text-xs mb-2">{event.type === 'themed-month' ? event.month : event.date}</p>
                      <p className="text-gray-500 text-xs font-inter line-clamp-2">{event.description}</p>
                      <span className="text-xs text-purple-400 font-semibold mt-2 inline-block">Learn More →</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Tournaments */}
            {activeTab === 'tournaments' && (
              <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
                {TOURNAMENTS.map((tournament, index) => (
                  <TournamentCard 
                    key={tournament.id} 
                    tournament={tournament} 
                    delay={index * 0.1}
                    onClick={() => handleItemClick(tournament, 'tournament')} 
                  />
                ))}
              </div>
            )}

            {/* Creators Program */}
            {activeTab === 'creators' && (
              <div>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div>
                    <h3 className="font-azonix font-bold text-lg sm:text-xl text-white">Featured Creators</h3>
                    <p className="text-gray-500 text-xs sm:text-sm">Meet our talented members</p>
                  </div>
                  {/* Wireframe button */}
                  <button className="flex items-center gap-1 px-3 py-1.5 sm:py-2 border border-purple-500/50 text-purple-300 rounded-lg text-xs font-semibold hover:bg-purple-500/20 transition-colors">
                    Apply <ArrowRight size={12} />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 sm:gap-4">
                  {CREATORS.map((creator, index) => (
                    <CreatorCard 
                      key={creator.id} 
                      creator={creator} 
                      delay={index * 0.1}
                      onClick={() => handleItemClick(creator, 'creator')} 
                    />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedItem && (
          <DetailModal item={selectedItem} type={selectedType} onClose={closeModal} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default CommunityFeatures;
